import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box,
  Card,
  Container,
  Typography,
  Grid,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Alert,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  LinearProgress,
  Snackbar,
  RadioGroup,
  Radio,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Tabs,
  Tab
} from '@mui/material';
import {
  PlayArrow,
  History,
  PendingActions,
  CheckCircle,
  QuestionAnswer,
  Timer,
  Score,
  School,
  AccessTime,
  Quiz,
  Search,
  Refresh,
  ViewList,
  CalendarToday,
  Check,
  Close,
  NavigateBefore,
  NavigateNext,
  Flag,
  DoneAll,
  Devices,
  Security,
  Computer,
  Error,
  Block,
  ListAlt,
  Description
} from '@mui/icons-material';
import { baseUrl, config, getUser, requestID } from "../../common/utils";
import { useHistory } from 'react-router-dom';
import { generateDeviceId } from '../../common/deviceFingerprint';
import { IonContent, IonPage } from '@ionic/react';

interface Exam {
  attempt_id: string;
  ex_id: string;
  ex_title: string;
  ex_description: string;
  ex_time_limit: number;
  ex_questlimit_display: number;
  question_display_status: string;
  display_score_status: string;
  level: string;
  status: string;
  start_time: string;
  end_time?: string;
  score?: number;
  total_questions?: number;
  duration?: number;
  branch_id: number;
  business_id: number;
  user_id?: number;
  loggedin_device_id_status?: number;
  id?: number;
}

interface Question {
  id: string;
  question: string;
  options: string[];
  correct_option?: number;
  question_type?: string;
  marks?: number;
}

interface ExamResult {
  attempt_id: string;
  score: number;
  total_questions: number;
  percentage: number;
  answers: {
    question_id: string;
    selected_option: string;
    is_correct: boolean;
  }[];
}

interface SavedAnswer {
  question_id: string;
  selected_option: string;
}

const StudentExamInterface: React.FC = () => {
  const history = useHistory();
  const [pendingExams, setPendingExams] = useState<Exam[]>([]);
  const [takenExams, setTakenExams] = useState<Exam[]>([]);
  const [availableExams, setAvailableExams] = useState<Exam[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingExams, setLoadingExams] = useState(false);
  const [error, setError] = useState('');
  const [selectedExam, setSelectedExam] = useState<Exam | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [examStarted, setExamStarted] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [attemptId, setAttemptId] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<Record<string, number>>({});
  const [examTimer, setExamTimer] = useState<NodeJS.Timeout | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [examResult, setExamResult] = useState<ExamResult | null>(null);
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  
  // Tab state for exam sections
  const [activeTab, setActiveTab] = useState(0);
  
  // Pagination states for exam lists
  const [pendingPage, setPendingPage] = useState(0);
  const [takenPage, setTakenPage] = useState(0);
  const [availablePage, setAvailablePage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  
  // Search states
  const [pendingSearch, setPendingSearch] = useState('');
  const [takenSearch, setTakenSearch] = useState('');
  const [availableSearch, setAvailableSearch] = useState('');
  
  // Notification
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });

  // Device mismatch dialog state
  const [deviceMismatchDialog, setDeviceMismatchDialog] = useState({
    open: false,
    examTitle: '',
    message: ''
  });

  // Fetch exams on component mount
  useEffect(() => {
    fetchExams();
  }, []);

  // Timer effect when exam is started
  useEffect(() => {
    if (examStarted && selectedExam && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            handleAutoSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      setExamTimer(timer);
      return () => clearInterval(timer);
    }
  }, [examStarted, selectedExam, timeRemaining]);

  const fetchExams = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch pending exams
      const pendingData = new FormData();
      pendingData.append("requestID", requestID());
      
      const pendingResponse = await axios.post(`${baseUrl()}Cbt_ExamAttempts/pending`, pendingData, config);

      console.log("Pending Exams:", pendingResponse.data);
      
      if (pendingResponse.data.status === true) {
        setPendingExams(pendingResponse.data.data || []);
      }
      
      // Fetch taken exams
      const takenData = new FormData();
      takenData.append("requestID", requestID());
      
      const takenResponse = await axios.post(`${baseUrl()}Cbt_ExamAttempts/taken`, takenData, config);
      console.log('taken', takenResponse)
      
      if (takenResponse.data.status === true) {
        setTakenExams(takenResponse.data.data || []);
      }
      
      // Fetch available exams
      const availableData = new FormData();
      availableData.append("requestID", requestID());
      
      const availableResponse = await axios.post(`${baseUrl()}Cbt_ExamAttempts/availableExams`, availableData, config);
      
      console.log("Available Exams:", availableResponse.data);
      if (availableResponse.data.status === true) {
        setAvailableExams(availableResponse.data.data || []);
      }
      
    } catch (err: any) {
      console.error('Error fetching exams:', err);
      setError('Failed to fetch exams. Please try again.');
      showSnackbar('Failed to fetch exams', 'error');
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const check_cbt_device = async (userId: string) => {
    const deviceId = generateDeviceId(userId);

    console.log('Saving device ID for user ID:', userId);
    try {
      const response = await axios.post(
        baseUrl() + "users/check_device",
        {
          user_id: userId,
          device_id: deviceId.base64,
          device_hash: await deviceId.hash, 
          requestID: requestID(),
        },
        config
      );
      
      console.log('Device ID saved successfully:', response);
      return response.data;
    } catch (err: any) {
      console.error('Failed to save device ID:', err);
      return false;
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleStartExamClick = (exam: Exam) => {
    setSelectedExam(exam);
    setConfirmDialogOpen(true);
  };

  const handleStartAvailableExam = async (exam: Exam) => {
    setSelectedExam(exam);
    setConfirmDialogOpen(true);
  };

  const fetchSavedAnswers = async (attemptId: string) => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("attempt_id", attemptId);
      
      const response = await axios.post(`${baseUrl()}Cbt_ExamAttempts/getSavedAnswers`, formData, config);
      console.log("Saved Answers Response:", response);
      
      if (response.data.status === true && response.data.data) {
        const savedAnswers = response.data.data;
        
        // Convert saved answers to the format used by selectedOptions
        const newSelectedOptions: Record<string, number> = {};
        
        savedAnswers.forEach((answer: SavedAnswer) => {
          if (answer.question_id && answer.selected_option) {
            // Convert option letter (a,b,c,d,e) to index (0,1,2,3,4)
            const optionIndex = answer.selected_option.toLowerCase().charCodeAt(0) - 97; // 'a' = 97
            
            if (optionIndex >= 0 && optionIndex <= 4) {
              newSelectedOptions[answer.question_id] = optionIndex;
            }
          }
        });
        
        return newSelectedOptions;
      }
      
      return {};
    } catch (err) {
      console.error('Error fetching saved answers:', err);
      return {};
    }
  };

  const handleConfirmStart = async () => {
    try {
      setConfirmDialogOpen(false);
      setLoadingExams(true);

      const user_id = getUser().loginData.user_id;
      const check_device_response = await check_cbt_device(user_id);
      const loggedin_device_id_status = selectedExam?.loggedin_device_id_status;

//console.log('selectedExam', selectedExam);
     // return;
      console.log('Device ID loggedin_device_id_status:', loggedin_device_id_status);
      console.log('Device ID check_device_status:', check_device_response.data);

      // Check if device ID is mismatched
      if (loggedin_device_id_status == 1 && check_device_response.data === 'mismatch') {
        setDeviceMismatchDialog({
          open: true,
          examTitle: selectedExam!.ex_title,
          message: 'You have started this exam on another device. You cannot write this exam on a different device.'
        });
        setLoadingExams(false);
        return;
      }

      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("ex_id", selectedExam!.ex_id);
      formData.append("ex_time_limit", selectedExam!.ex_time_limit.toString());
      
      const response = await axios.post(`${baseUrl()}Cbt_ExamAttempts/start`, formData, config);
      console.log("Start Exam Response:", response.data);
      
      if (response.data.status === true) {
        const newAttemptId = response.data.attempt_id;
        setAttemptId(newAttemptId);
        setExamStarted(true);
        
        const remainingMinutes = response.data.remainingMinutes;
        console.log('Remaining minutes:', remainingMinutes);
        
        setTimeRemaining(Number(remainingMinutes) * 60);
        
        // Fetch questions first
        await fetchExamQuestions(selectedExam!.ex_id,selectedExam?.cou_id);
        
        // Then fetch and apply saved answers
        const savedAnswers = await fetchSavedAnswers(newAttemptId);
        
        if (Object.keys(savedAnswers).length > 0) {
          setSelectedOptions(prev => ({
            ...prev,
            ...savedAnswers
          }));
          
          showSnackbar(`${Object.keys(savedAnswers).length} previously answered questions restored`, 'info');
        }
      }
    } catch (err: any) {
      console.error('Error starting exam:', err);
      showSnackbar(err.response?.data?.message || 'Failed to start exam', 'error');
      setLoadingExams(false);
    }
  };

  const fetchExamQuestions = async (examId: string,cou_id: string) => {
    try {
      setLoadingExams(true);
      
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("ex_id", examId);
       formData.append("cou_id", cou_id); 
      
      console.log("Fetching questions for exam:", examId);
      
      // Fetch questions from questions_cbt table
      const response = await axios.post(`${baseUrl()}Cbt_ExamAttempts/getExamQuestions`, formData, config);
      
      console.log("Questions API Response:", response);
      
      if (response.data.status === true && response.data.data && response.data.data.length > 0) {
        const questionsData = response.data.data;
        
        console.log("Raw questions data:", questionsData);
        
        // Map the API response to your Question interface
        const fetchedQuestions: Question[] = questionsData.map((q: any, index: number) => {
          // Create options array from option_a through option_e
          const options = [];
          if (q.option_a && q.option_a.trim() !== '') options.push(q.option_a);
          if (q.option_b && q.option_b.trim() !== '') options.push(q.option_b);
          if (q.option_c && q.option_c.trim() !== '') options.push(q.option_c);
          if (q.option_d && q.option_d.trim() !== '') options.push(q.option_d);
          if (q.option_e && q.option_e.trim() !== '') options.push(q.option_e);
          
          return {
            id: q.question_id || q.id || `q${index + 1}`,
            question: q.question || `Question ${index + 1}`,
            options: options.length > 0 ? options : ['Option A', 'Option B', 'Option C', 'Option D'],
            question_type: 'multiple_choice',
            marks: 1
          };
        });
        
        console.log("Mapped Questions:", fetchedQuestions);
        
        setQuestions(fetchedQuestions);
        setLoadingExams(false);
      } else {
        setLoadingExams(false);
        showSnackbar('No questions found for this exam', 'warning');
      }
    } catch (err: any) {
      console.error('Error fetching questions:', err);
      showSnackbar('Failed to load questions', 'error');
      setLoadingExams(false);
    }
  };

  const handleAnswerSelect = (questionId: string, optionIndex: number) => {
    const newSelectedOptions = {
      ...selectedOptions,
      [questionId]: optionIndex
    };
    setSelectedOptions(newSelectedOptions);
    
    // Save answer to backend
    saveAnswer(questionId, optionIndex);
  };

  const saveAnswer = async (questionId: string, selectedOption: number) => {
    try {
      const data = {
        requestID: requestID(),
        attempt_id: attemptId,
        question_id: questionId,
        selected_option: String.fromCharCode(97 + selectedOption),
        ex_id:  selectedExam?.ex_id,
      }

      console.log({data})
      const res = await axios.post(`${baseUrl()}Cbt_ExamAttempts/answer`, data, config);
      console.log({res})
      
    } catch (err) {
      console.error('Failed to save answer:', err);
      showSnackbar('Failed to save answer. Please check your connection.', 'warning');
    }
  };

  const handleEndExam = async () => {
    if (window.confirm('Are you sure you want to end the exam? This action cannot be undone.')) {
      await submitExam();
    }
  };

  const handleAutoSubmit = async () => {
    showSnackbar('Time is up! Submitting your exam...', 'info');
    await submitExam();
  };

  const submitExam = async () => {
    try {
      setLoadingExams(true);
      
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("attempt_id", attemptId);
      
      const response = await axios.post(`${baseUrl()}Cbt_ExamAttempts/end`, formData, config);
      console.log(response)
      
      if (response.data.status === true) {
        // Fetch detailed results
        await fetchExamResults(attemptId);
        setResultDialogOpen(true);
        
        // Clean up
        if (examTimer) {
          clearInterval(examTimer);
        }
        setExamStarted(false);
        setSelectedExam(null);
        setAttemptId('');
        setCurrentQuestionIndex(0);
        setSelectedOptions({});
        setTimeRemaining(0);
        
        // Refresh exam lists
        fetchExams();
      }
    } catch (err: any) {
      console.error('Error submitting exam:', err);
      showSnackbar(err.response?.data?.message || 'Failed to submit exam', 'error');
    } finally {
      setLoadingExams(false);
    }
  };

  const fetchExamResults = async (attemptId: string) => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("attempt_id", attemptId);
      
      const response = await axios.post(`${baseUrl()}Cbt_ExamAttempts/scoreByQuestion`, formData, config);
      console.log("Exam Results Response:", response.data);
      
      if (response.data.status === true) {
        const resultData = response.data.data;
        
        // Map the API response to your ExamResult interface
        setExamResult({
          attempt_id: resultData.attempt_id || attemptId,
          score: resultData.correct || 0,
          total_questions: resultData.total_questions || (resultData.answers?.length || 0),
          percentage: resultData.score_percent || 0,
          answers: resultData.answers || []
        });
      } else {
        // Fallback if data structure is different
        const answers = response.data.data || [];
        const correctCount = answers.filter((a: any) => a.is_correct).length;
        const totalCount = answers.length;
        
        setExamResult({
          attempt_id: attemptId,
          score: correctCount,
          total_questions: totalCount,
          percentage: totalCount > 0 ? (correctCount / totalCount) * 100 : 0,
          answers: answers.map((a: any) => ({
            question_id: a.question_id,
            selected_option: a.selected_option,
            is_correct: a.is_correct
          }))
        });
      }
    } catch (err) {
      console.error('Error fetching results:', err);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handleQuestionClick = (index: number) => {
    setCurrentQuestionIndex(index);
  };

  const formatTime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getQuestionStatus = (index: number) => {
    const questionId = questions[index]?.id;
    return selectedOptions[questionId] !== undefined ? 'answered' : 'unanswered';
  };

  const getAnsweredCount = () => {
    return Object.keys(selectedOptions).length;
  };

  const handleViewExamDetails = (exam: Exam) => {
    // Show detailed results for this specific exam
    setSelectedExam(exam);
    
    // Create exam result from the taken exam data
    const examResult: ExamResult = {
      attempt_id: exam.attempt_id,
      score: exam.correct || 0,
      total_questions: exam.total_questions || 0,
      percentage: exam.score_percent || 0,
      answers: exam.answers || []
    };
    
    setExamResult(examResult);
    setResultDialogOpen(true);
  };

  const filteredPendingExams = pendingExams.filter(exam => 
    exam.ex_title?.toLowerCase().includes(pendingSearch?.toLowerCase()) ||
    exam.ex_id?.toLowerCase().includes(pendingSearch?.toLowerCase()) ||
    exam.level?.toLowerCase().includes(pendingSearch?.toLowerCase())
  );

  const filteredTakenExams = takenExams.filter(exam => 
    (exam?.exam_title || exam?.ex_title || '').toLowerCase().includes(takenSearch.toLowerCase()) ||
    (exam?.exam_id || exam?.ex_id || '').toLowerCase().includes(takenSearch.toLowerCase()) ||
    (exam?.exam_description || '').toLowerCase().includes(takenSearch.toLowerCase())
  );

  const filteredAvailableExams = availableExams.filter(exam => 
    exam.ex_title?.toLowerCase().includes(availableSearch?.toLowerCase()) ||
    exam.ex_id?.toLowerCase().includes(availableSearch?.toLowerCase()) ||
    exam.level?.toLowerCase().includes(availableSearch?.toLowerCase()) ||
    exam.ex_description?.toLowerCase().includes(availableSearch?.toLowerCase())
  );

  const pendingPagination = filteredPendingExams.slice(
    pendingPage * rowsPerPage,
    pendingPage * rowsPerPage + rowsPerPage
  );

  const takenPagination = filteredTakenExams.slice(
    takenPage * rowsPerPage,
    takenPage * rowsPerPage + rowsPerPage
  );

  const availablePagination = filteredAvailableExams.slice(
    availablePage * rowsPerPage,
    availablePage * rowsPerPage + rowsPerPage
  );

  const renderAvailableExamsTab = () => (
    <Card sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', bgcolor: 'white' }}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ListAlt color="info" />
            Available Exams
            <Chip 
              label={availableExams.length} 
              color="info" 
              size="small" 
              sx={{ ml: 1 }}
            />
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              placeholder="Search available exams..."
              value={availableSearch}
              onChange={(e) => setAvailableSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 250 }}
            />
            <Tooltip title="Refresh">
              <IconButton onClick={fetchExams} size="small">
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        {availableExams.length === 0 ? (
          <Alert severity="info">
            No available exams at the moment. Check back later!
          </Alert>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Exam Title</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Course/Level</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Questions</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {availablePagination.map((exam) => (
                    <TableRow key={exam.id || exam.ex_id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {exam.ex_title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {exam.ex_id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200 }}>
                          {exam.ex_description || 'No description available'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <School fontSize="small" color="action" />
                          <Typography variant="body2">
                            {exam.level}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AccessTime fontSize="small" color="action" />
                          <Typography variant="body2">
                            {exam.ex_time_limit} minutes
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={`${exam.ex_questlimit_display} Qs`} 
                          size="small" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label="Available" 
                          color="success" 
                          size="small" 
                          sx={{ fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<PlayArrow />}
                          onClick={() => handleStartAvailableExam(exam)}
                          sx={{ minWidth: 120 }}
                        >
                          Start Exam
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredAvailableExams.length}
              rowsPerPage={rowsPerPage}
              page={availablePage}
              onPageChange={(_, newPage) => setAvailablePage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setAvailablePage(0);
              }}
            />
          </>
        )}
      </Box>
    </Card>
  );

  const renderPendingExamsTab = () => (
    <Card sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', bgcolor: 'white' }}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PendingActions color="warning" />
            Pending Exams
            <Chip 
              label={pendingExams.length} 
              color="warning" 
              size="small" 
              sx={{ ml: 1 }}
            />
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              placeholder="Search pending exams..."
              value={pendingSearch}
              onChange={(e) => setPendingSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 250 }}
            />
            <Tooltip title="Refresh">
              <IconButton onClick={fetchExams} size="small">
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        {pendingExams.length === 0 ? (
          <Alert severity="info">
            No pending exams available. Check back later!
          </Alert>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Exam Title</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Course/Level</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Questions</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingPagination.map((exam) => (
                    <TableRow key={exam.attempt_id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {exam.ex_title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {exam.ex_id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <School fontSize="small" color="action" />
                          <Typography variant="body2">
                            Level {exam.level.replace('year', '')}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AccessTime fontSize="small" color="action" />
                          <Typography variant="body2">
                            {exam.ex_time_limit} minutes
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={`${exam.ex_questlimit_display} Qs`} 
                          size="small" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label="Ready to Start" 
                          color="warning" 
                          size="small" 
                          sx={{ fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<PlayArrow />}
                          onClick={() => handleStartExamClick(exam)}
                          sx={{ minWidth: 120 }}
                        >
                          Start Exam
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredPendingExams.length}
              rowsPerPage={rowsPerPage}
              page={pendingPage}
              onPageChange={(_, newPage) => setPendingPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPendingPage(0);
                setTakenPage(0);
                setAvailablePage(0);
              }}
            />
          </>
        )}
      </Box>
    </Card>
  );

  const renderTakenExamsTab = () => (
    <Card sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', bgcolor: 'white' }}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <History color="primary" />
            Completed Exams
            <Chip 
              label={takenExams.length} 
              color="primary" 
              size="small" 
              sx={{ ml: 1 }}
            />
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              placeholder="Search completed exams..."
              value={takenSearch}
              onChange={(e) => setTakenSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 250 }}
            />
          </Box>
        </Box>
        
        {takenExams.length === 0 ? (
          <Alert severity="info">
            No completed exams yet. Start your first exam!
          </Alert>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Exam Title</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date Taken</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Score</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Time Spent</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Correct/Total</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {takenPagination.map((exam) => (
                    <TableRow key={exam.attempt_id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {exam.exam_title || exam.ex_title || 'Untitled Exam'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {exam.exam_id || exam.ex_id}
                          </Typography>
                          {exam.exam_description && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {exam.exam_description}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarToday fontSize="small" color="action" />
                          <Typography variant="body2">
                            {formatDate(exam.end_time || exam.start_time)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Score fontSize="small" color={exam.score_percent && exam.score_percent >= 50 ? 'success' : 'error'} />
                          <Typography 
                            variant="body2" 
                            fontWeight={600}
                            color={exam.score_percent && exam.score_percent >= 50 ? 'success.main' : 'error.main'}
                          >
                            {exam.score_percent !== undefined ? `${exam.score_percent}%` : 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {exam.time_spent || (exam.duration ? `${Math.floor(exam.duration / 60)}m ${exam.duration % 60}s` : 'N/A')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircle fontSize="small" color="success" />
                          <Typography variant="body2">
                            {exam.correct || 0}/{exam.total_questions || 0}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label="Completed" 
                          color="success" 
                          size="small" 
                          sx={{ fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small" 
                            onClick={() => handleViewExamDetails(exam)}
                          >
                            <ViewList />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredTakenExams.length}
              rowsPerPage={rowsPerPage}
              page={takenPage}
              onPageChange={(_, newPage) => setTakenPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setTakenPage(0);
              }}
            />
          </>
        )}
      </Box>
    </Card>
  );

  if (initialLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '80vh',
        bgcolor: 'white'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ 
      py: 3,
      bgcolor: 'white',
      minHeight: '100vh'
    }}>
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3, bgcolor: 'white' }} 
          action={
            <Button color="inherit" size="small" onClick={fetchExams}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {examStarted ? (
        <ExamInterface
          exam={selectedExam!}
          currentQuestion={questions[currentQuestionIndex]}
          currentIndex={currentQuestionIndex}
          totalQuestions={questions.length}
          onPrevious={handlePreviousQuestion}
          onNext={handleNextQuestion}
          onAnswerSelect={handleAnswerSelect}
          selectedOption={selectedOptions[questions[currentQuestionIndex]?.id]}
          onEndExam={handleEndExam}
          examTime={formatTime(timeRemaining)}
          questionStatuses={questions.map((_, idx) => getQuestionStatus(idx))}
          onQuestionClick={handleQuestionClick}
          answeredCount={getAnsweredCount()}
          loading={loadingExams}
          timeRemaining={timeRemaining}
          totalTime={selectedExam!.ex_time_limit * 60}
        />
      ) : (
        <>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Quiz fontSize="large" color="primary" />
              Exam Portal
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Manage your available, pending and completed exams
            </Typography>
          </Box>

          {/* Tabs for different exam types */}
          <Card sx={{ 
            mb: 3, 
            borderRadius: 2, 
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            bgcolor: 'white'
          }}>
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              variant="fullWidth"
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  py: 2,
                }
              }}
            >
              <Tab 
                icon={<ListAlt />} 
                iconPosition="start" 
                label={`Available Exams (${availableExams.length})`} 
                sx={{ 
                  color: activeTab === 0 ? 'primary.main' : 'text.secondary',
                  borderBottom: activeTab === 0 ? 2 : 0,
                  borderColor: 'primary.main'
                }}
              />
              <Tab 
                icon={<PendingActions />} 
                iconPosition="start" 
                label={`Pending Exams (${pendingExams.length})`} 
                sx={{ 
                  color: activeTab === 1 ? 'primary.main' : 'text.secondary',
                  borderBottom: activeTab === 1 ? 2 : 0,
                  borderColor: 'primary.main'
                }}
              />
              <Tab 
                icon={<History />} 
                iconPosition="start" 
                label={`Completed Exams (${takenExams.length})`} 
                sx={{ 
                  color: activeTab === 2 ? 'primary.main' : 'text.secondary',
                  borderBottom: activeTab === 2 ? 2 : 0,
                  borderColor: 'primary.main'
                }}
              />
            </Tabs>
            
            <Box sx={{ p: 3, bgcolor: 'white' }}>
              {activeTab === 0 && renderAvailableExamsTab()}
              {activeTab === 1 && renderPendingExamsTab()}
              {activeTab === 2 && renderTakenExamsTab()}
            </Box>
          </Card>
        </>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PlayArrow />
            <Typography variant="h6">Start Exam</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, bgcolor: 'white' }}>
          {selectedExam && (
            <>
              <DialogContentText gutterBottom>
                Are you ready to start the exam? Once started, the timer will begin counting down.
              </DialogContentText>
              
              <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'white' }}>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <Quiz fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Exam Title" 
                      secondary={selectedExam.ex_title}
                      secondaryTypographyProps={{ fontWeight: 600 }}
                    />
                  </ListItem>
                  <Divider component="li" />
                  {selectedExam.ex_description && (
                    <>
                      <ListItem>
                        <ListItemIcon>
                          <Description fontSize="small" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Description" 
                          secondary={selectedExam.ex_description}
                        />
                      </ListItem>
                      <Divider component="li" />
                    </>
                  )}
                  <ListItem>
                    <ListItemIcon>
                      <AccessTime fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Duration" 
                      secondary={`${selectedExam.ex_time_limit} minutes`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <QuestionAnswer fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Questions" 
                      secondary={`${selectedExam.ex_questlimit_display} questions`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <School fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Level" 
                      secondary={selectedExam.level}
                    />
                  </ListItem>
                </List>
              </Paper>
              
              <Alert severity="info" sx={{ mt: 2, bgcolor: 'info.light' }}>
                <Typography variant="body2">
                  <strong>Instructions:</strong> Ensure you have a stable internet connection. 
                  The exam will auto-submit when time is up. You can navigate between questions using the navigation panel.
                </Typography>
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: 'white' }}>
          <Button onClick={() => setConfirmDialogOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmStart} 
            variant="contained" 
            color="primary"
            startIcon={<PlayArrow />}
          >
            Start Exam Now
          </Button>
        </DialogActions>
      </Dialog>

      {/* Device Mismatch Dialog */}
      <Dialog
        open={deviceMismatchDialog.open}
        onClose={() => setDeviceMismatchDialog({ ...deviceMismatchDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          bgcolor: 'error.main', 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Error sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6">Device Security Alert</Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Exam Security Violation
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3, bgcolor: 'white' }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'error.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                border: '3px solid',
                borderColor: 'error.main'
              }}
            >
              <Devices sx={{ fontSize: 40, color: 'error.dark' }} />
            </Box>
            
            <Typography variant="h6" color="error" gutterBottom>
              Device Access Restricted
            </Typography>
            
            <Typography variant="body2" color="text.secondary" paragraph>
              For security reasons, this exam can only be taken from the device where it was started.
            </Typography>
          </Box>
          
          <Card variant="outlined" sx={{ mb: 3, borderColor: 'error.light', bgcolor: 'error.50' }}>
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Computer color="error" />
                    <Typography variant="body2" fontWeight={600} color="error">
                      Exam:
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={9}>
                  <Typography variant="body1" fontWeight={600}>
                    {deviceMismatchDialog.examTitle}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Card>
          
          <Box sx={{ bgcolor: 'warning.light', p: 2, borderRadius: 1, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Security color="warning" sx={{ mt: 0.5 }} />
              <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Security Policy
                </Typography>
                <Typography variant="body2">
                  To maintain exam integrity and prevent unauthorized access, each exam is 
                  locked to the specific device where it was initiated. This ensures fair 
                  assessment conditions for all students.
                </Typography>
              </Box>
            </Box>
          </Box>
          
          <Alert severity="error" sx={{ mb: 2, bgcolor: 'error.light' }}>
            <Typography variant="body2">
              <strong>Important:</strong> You have started this exam on another device. 
              You cannot write this exam on a different device for security reasons.
            </Typography>
          </Alert>
          
          <Alert severity="info" sx={{ bgcolor: 'info.light' }}>
            <Typography variant="body2">
              <strong>Solution:</strong> Please use the original device where you started 
              this exam to continue. If you believe this is an error, contact your 
              exam administrator for assistance.
            </Typography>
          </Alert>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, justifyContent: 'center', bgcolor: 'white' }}>
          <Button
            variant="contained"
            color="error"
            onClick={() => setDeviceMismatchDialog({ ...deviceMismatchDialog, open: false })}
            startIcon={<Block />}
            sx={{ minWidth: 200, py: 1 }}
          >
            Close & Return to Exam List
          </Button>
        </DialogActions>
      </Dialog>

      {/* Results Dialog */}
      <Dialog
        open={resultDialogOpen}
        onClose={() => setResultDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Score />
            <Typography variant="h6">Exam Results</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, bgcolor: 'white' }}>
          {examResult && (
            <>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 3, textAlign: 'center', bgcolor: 'white' }}>
                    <Typography variant="h3" fontWeight={700} color="primary">
                      {examResult.percentage.toFixed(1)}%
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                      Overall Score
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={examResult.percentage}
                      sx={{ height: 8, borderRadius: 4, mt: 2 }}
                    />
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 3, bgcolor: 'white' }}>
                    <List dense>
                      <ListItem>
                        <ListItemText 
                          primary="Correct Answers" 
                          secondary={
                            <Typography variant="h5" color="success.main">
                             {examResult.score} / {examResult.total_questions}
                             {/* {examResult.score}  */}

                            </Typography>
                          }
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Attempt ID" 
                          secondary={examResult.attempt_id}
                          secondaryTypographyProps={{ fontFamily: 'monospace' }}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText 
                          primary="Score" 
                          secondary={
                            <Typography variant="body1">
                              {examResult.score} correct, {examResult.total_questions - examResult.score} incorrect
                            </Typography>
                          }
                        />
                      </ListItem>
                    </List>
                  </Card>
                </Grid>
                
                {/* <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Answer Summary ({examResult.answers.length} questions)
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Question</strong></TableCell>
                          <TableCell align="center"><strong>Your Answer</strong></TableCell>
                          <TableCell align="center"><strong>Status</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {examResult.answers.map((answer, index) => {
                          const question = questions.find(q => q.id === answer.question_id);
                          
                          return (
                            <TableRow key={index}>
                              <TableCell>
                                {question ? question.question : `Question ${index + 1}`}
                              </TableCell>
                              <TableCell align="center">
                                <Chip 
                                  label={answer.selected_option?.toUpperCase() || 'N/A'} 
                                  color={answer.is_correct ? "success" : "error"}
                                  variant="outlined"
                                  size="small"
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  icon={answer.is_correct ? <Check /> : <Close />}
                                  label={answer.is_correct ? 'Correct' : 'Incorrect'}
                                  color={answer.is_correct ? 'success' : 'error'}
                                  size="small"
                                  variant="outlined"
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid> */}
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: 'white' }}>
          <Button 
            onClick={() => setResultDialogOpen(false)} 
            variant="contained" 
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

// Exam Interface Component
interface ExamInterfaceProps {
  exam: Exam;
  currentQuestion: Question;
  currentIndex: number;
  totalQuestions: number;
  onPrevious: () => void;
  onNext: () => void;
  onAnswerSelect: (questionId: string, optionIndex: number) => void;
  selectedOption?: number;
  onEndExam: () => void;
  examTime: string;
  questionStatuses: string[];
  onQuestionClick: (index: number) => void;
  answeredCount: number;
  loading: boolean;
  timeRemaining: number;
  totalTime: number;
}

const ExamInterface: React.FC<ExamInterfaceProps> = ({
  exam,
  currentQuestion,
  currentIndex,
  totalQuestions,
  onPrevious,
  onNext,
  onAnswerSelect,
  selectedOption,
  onEndExam,
  examTime,
  questionStatuses,
  onQuestionClick,
  answeredCount,
  loading,
  timeRemaining,
  totalTime
}) => {
  const timePercentage = (timeRemaining / totalTime) * 100;

  if (loading || !currentQuestion) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '70vh',
        bgcolor: 'white'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <IonPage>
      <IonContent>
<Box sx={{ bgcolor: 'white' }}>
      {/* Exam Header */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', bgcolor: 'white' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              {exam.ex_title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Chip 
                icon={<School />} 
                label={`Level ${exam.level}`} 
                size="small" 
                variant="outlined"
              />
              <Chip 
                icon={<QuestionAnswer />} 
                label={`Question ${currentIndex + 1} of ${totalQuestions}`} 
                size="small" 
                variant="outlined"
              />
              <Chip 
                icon={<CheckCircle />} 
                label={`${answeredCount} answered`} 
                color="primary" 
                size="small" 
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
              <Card variant="outlined" sx={{ p: 1.5, minWidth: 140, bgcolor: 'white' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Timer color={timePercentage < 20 ? 'error' : 'primary'} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Time Remaining
                    </Typography>
                    <Typography variant="h6" fontWeight={600} color={timePercentage < 20 ? 'error.main' : 'primary.main'}>
                      {examTime}
                    </Typography>
                  </Box>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={timePercentage}
                  color={timePercentage < 20 ? 'error' : 'primary'}
                  sx={{ height: 4, borderRadius: 2, mt: 1 }}
                />
              </Card>
              
              <Button
                variant="contained"
                color="error"
                onClick={onEndExam}
                startIcon={<DoneAll />}
                sx={{ height: 'fit-content' }}
              >
                Submit Exam
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        {/* Question Panel */}
        <Grid item xs={12} md={8}>
          <Card sx={{ p: 3, borderRadius: 2, height: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', bgcolor: 'white' }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" color="primary" fontWeight={600}>
                QUESTION {currentIndex + 1}
              </Typography>
              <Typography variant="h6" sx={{ mt: 1, mb: 3 }}>
                {currentQuestion.question}
              </Typography>
              
              <RadioGroup
                value={selectedOption !== undefined ? selectedOption.toString() : ''}
                onChange={(e) => onAnswerSelect(currentQuestion.id, parseInt(e.target.value))}
              >
                {currentQuestion.options.map((option, index) => (
                  <Paper
                    key={index}
                    elevation={selectedOption === index ? 2 : 0}
                    sx={{
                      p: 2,
                      mb: 2,
                      border: 2,
                      borderColor: selectedOption === index ? 'primary.main' : 'transparent',
                      bgcolor: selectedOption === index ? 'primary.light' : 'grey.50',
                      transition: 'all 0.2s',
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'action.hover',
                        borderColor: 'primary.light',
                      },
                    }}
                    onClick={() => onAnswerSelect(currentQuestion.id, index)}
                  >
                    <FormControlLabel
                      value={index.toString()}
                      control={<Radio />}
                      label={
                        <Typography sx={{ ml: 1 }}>
                          <Box component="span" sx={{ fontWeight: 600, mr: 1 }}>
                            {String.fromCharCode(65 + index)}.
                          </Box>
                          {option}
                        </Typography>
                      }
                      sx={{ width: '100%', m: 0 }}
                    />
                  </Paper>
                ))}
              </RadioGroup>
            </Box>

            {/* Navigation Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
              <Button
                variant="outlined"
                startIcon={<NavigateBefore />}
                onClick={onPrevious}
                disabled={currentIndex === 0}
                sx={{ minWidth: 120 }}
              >
                Previous
              </Button>
              
              {selectedOption !== undefined && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle color="success" />
                  <Typography variant="body2" color="success.main" fontWeight={600}>
                    Answer Saved
                  </Typography>
                </Box>
              )}
              
              <Button
                variant="outlined"
                endIcon={<NavigateNext />}
                onClick={onNext}
                disabled={currentIndex === totalQuestions - 1}
                sx={{ minWidth: 120 }}
              >
                Next
              </Button>
            </Box>
          </Card>
        </Grid>

        {/* Navigation Panel */}
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3, borderRadius: 2, height: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', bgcolor: 'white' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ViewList />
              Question Navigation
            </Typography>
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Click on any question number to jump to that question
            </Typography>
            
            <Box sx={{ mt: 3 }}>
              <Grid container spacing={1}>
                {questionStatuses.map((status, index) => (
                  <Grid item xs={3} sm={2} key={index}>
                    <Tooltip title={`Question ${index + 1}`}>
                      <IconButton
                        onClick={() => onQuestionClick(index)}
                        sx={{
                          width: '100%',
                          aspectRatio: '1/1',
                          border: 2,
                          borderColor: currentIndex === index ? 'primary.main' : 'divider',
                          bgcolor: status === 'answered' ? 'primary.light' : 'transparent',
                          '&:hover': {
                            bgcolor: 'action.hover',
                          },
                        }}
                      >
                        <Typography 
                          variant="body2" 
                          fontWeight={currentIndex === index ? 600 : 400}
                          color={currentIndex === index ? 'primary.main' : 'text.primary'}
                        >
                          {index + 1}
                        </Typography>
                        {status === 'answered' && (
                          <CheckCircle 
                            fontSize="small" 
                            color="success" 
                            sx={{ position: 'absolute', top: 2, right: 2 }} 
                          />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Grid>
                ))}
              </Grid>
            </Box>
            
            {/* Progress Stats */}
            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: 'white' }}>
                    <Typography variant="h4" color="primary" fontWeight={700}>
                      {answeredCount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Answered
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: 'white' }}>
                    <Typography variant="h4" color="text.secondary" fontWeight={700}>
                      {totalQuestions - answeredCount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Remaining
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
              
              <LinearProgress 
                variant="determinate" 
                value={(answeredCount / totalQuestions) * 100}
                sx={{ height: 8, borderRadius: 4, mt: 2 }}
              />
              
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                Progress: {Math.round((answeredCount / totalQuestions) * 100)}%
              </Typography>
            </Box>
            
            {/* Quick Actions */}
            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                color="error"
                fullWidth
                onClick={onEndExam}
                startIcon={<DoneAll />}
                sx={{ mb: 1 }}
              >
                Submit All Answers
              </Button>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<Flag />}
                onClick={() => {
                  // Mark for review functionality
                }}
              >
                Mark for Review
              </Button>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
      </IonContent>
    </IonPage>
    
  );
};

export default StudentExamInterface;